'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HalfLifeCalculator() {
  const t = useTranslations("tools.halfLifeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Main calculator state
  const [mainInputs, setMainInputs] = useState({
    nt: '',
    n0: '',
    t: '',
    t12: ''
  });
  const [mainResults, setMainResults] = useState<{
    value: number;
    label: string;
    formula: string;
  } | null>(null);

  // Conversion calculator state
  const [convInputs, setConvInputs] = useState({
    halflife: '',
    meanlifetime: '',
    decayconstant: ''
  });
  const [convResults, setConvResults] = useState<{
    halflife: number;
    meanlifetime: number;
    decayconstant: number;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#15803d',
    successBg: 'rgba(240, 253, 244, 0.9)',
    warning: '#92400e',
    warningBg: 'rgba(254, 243, 199, 0.9)',
    info: '#1e40af',
    infoBg: '#eff6ff',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: '#2563eb', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    subtitle: getResponsiveStyle(
      { color: colors.textSecondary },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.25rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' },
      { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem'
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '0.25rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        background: '#f8fafc',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.875rem 1rem', fontSize: '1.0625rem' },
      { padding: '0.875rem 1.125rem', fontSize: '1.125rem' }
    ),
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginTop: '1rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    calculateButton: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 2rem', fontSize: '1.0625rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' }
    ),
    clearButton: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: '#475569'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 2rem', fontSize: '1.0625rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' }
    ),
    resultsCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '1rem',
        border: '2px solid #86efac',
        marginTop: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    conversionResultsCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '1rem',
        border: '2px solid #fbbf24',
        marginTop: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        marginBottom: '1rem'
      },
      { padding: '1rem', flexDirection: 'column' as const, gap: '0.5rem', alignItems: 'flex-start' },
      { padding: '1.125rem', flexDirection: 'row' as const, gap: '0', alignItems: 'center' },
      { padding: '1.25rem', flexDirection: 'row' as const, gap: '0', alignItems: 'center' }
    ),
    resultLabel: getResponsiveStyle(
      { fontWeight: 600, color: '#334155' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    formulaDisplay: getResponsiveStyle(
      {
        background: '#f8fafc',
        borderRadius: '0.5rem',
        marginTop: '1rem',
        fontFamily: "'Courier New', monospace",
        color: '#475569',
        textAlign: 'center' as const,
        overflowX: 'auto' as const
      },
      { padding: '0.875rem', fontSize: '0.8rem' },
      { padding: '1rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.95rem' }
    ),
    infoNote: getResponsiveStyle(
      {
        background: colors.infoBg,
        borderLeft: '4px solid #2563eb',
        borderRadius: '0.5rem',
        marginTop: '1.5rem',
        color: colors.info,
        lineHeight: 1.6
      },
      { padding: '0.875rem', fontSize: '0.85rem' },
      { padding: '1rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', marginBottom: '2rem' },
      { padding: '2.5rem', marginBottom: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', marginBottom: '1rem', color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6
    },
    faqItem: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      { marginBottom: '1.75rem' },
      { marginBottom: '2rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.3s ease'
    }
  };

  const handleMainCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const nt = parseFloat(mainInputs.nt);
    const n0 = parseFloat(mainInputs.n0);
    const t1 = parseFloat(mainInputs.t);
    const t12 = parseFloat(mainInputs.t12);

    const values = [nt, n0, t1, t12];
    const filledCount = values.filter(v => !isNaN(v)).length;

    if (filledCount !== 3) {
      alert(t('errorThreeValues'));
      return;
    }

    let result: number;
    let resultLabel: string;
    let formula: string;

    if (isNaN(nt)) {
      result = n0 * Math.pow(0.5, t1 / t12);
      resultLabel = t('remainingQuantity');
      formula = `Nₜ = ${n0} × (1/2)^(${t1}/${t12}) = ${result.toFixed(6)}`;
    } else if (isNaN(n0)) {
      result = nt / Math.pow(0.5, t1 / t12);
      resultLabel = t('initialQuantity');
      formula = `N₀ = ${nt} / (1/2)^(${t1}/${t12}) = ${result.toFixed(6)}`;
    } else if (isNaN(t1)) {
      result = t12 * (Math.log(nt / n0) / Math.log(0.5));
      resultLabel = t('timeElapsed');
      formula = `t = ${t12} × log(${nt}/${n0}) / log(0.5) = ${result.toFixed(6)}`;
    } else {
      result = t1 * Math.log(2) / Math.log(n0 / nt);
      resultLabel = t('halfLife');
      formula = `t₁/₂ = ${t1} × ln(2) / ln(${n0}/${nt}) = ${result.toFixed(6)}`;
    }

    if (result < 0 || !isFinite(result)) {
      alert(t('errorInvalidCalculation'));
      return;
    }

    setMainResults({ value: result, label: resultLabel, formula });
  };

  const handleConversionCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const halflife = parseFloat(convInputs.halflife);
    const meanlifetime = parseFloat(convInputs.meanlifetime);
    const decayconstant = parseFloat(convInputs.decayconstant);

    const values = [halflife, meanlifetime, decayconstant];
    const filledCount = values.filter(v => !isNaN(v)).length;

    if (filledCount !== 1) {
      alert(t('errorOneValue'));
      return;
    }

    let t12: number, tau: number, lambda: number;
    const ln2 = Math.log(2);

    if (!isNaN(halflife)) {
      t12 = halflife;
      lambda = ln2 / halflife;
      tau = halflife / ln2;
    } else if (!isNaN(meanlifetime)) {
      tau = meanlifetime;
      lambda = 1 / meanlifetime;
      t12 = meanlifetime * ln2;
    } else {
      lambda = decayconstant;
      t12 = ln2 / decayconstant;
      tau = 1 / decayconstant;
    }

    if (t12 <= 0 || tau <= 0 || lambda <= 0 || !isFinite(t12) || !isFinite(tau) || !isFinite(lambda)) {
      alert(t('errorPositiveValue'));
      return;
    }

    setConvResults({ halflife: t12, meanlifetime: tau, decayconstant: lambda });
  };

  const handleMainClear = () => {
    setMainInputs({ nt: '', n0: '', t: '', t12: '' });
    setMainResults(null);
  };

  const handleConversionClear = () => {
    setConvInputs({ halflife: '', meanlifetime: '', decayconstant: '' });
    setConvResults(null);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool Section */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>⚛️ {t('toolTitle')}</h2>
            <p style={styles.subtitle}>{t('toolSubtitle')}</p>
          </div>

          {/* Main Calculator */}
          <div style={styles.calculatorCard}>
            <h3 style={styles.h3}>🧪 {t('mainCalculatorTitle')}</h3>
            <p style={{ ...styles.subtitle, marginBottom: '1.5rem' }}>{t('mainCalculatorDesc')}</p>

            <form onSubmit={handleMainCalculate}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="nt">
                    {t('remainingQuantity')} (N<sub>t</sub>)
                  </label>
                  <input
                    type="number"
                    id="nt"
                    step="any"
                    placeholder={t('placeholderRemaining')}
                    value={mainInputs.nt}
                    onChange={(e) => setMainInputs({...mainInputs, nt: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="n0">
                    {t('initialQuantity')} (N<sub>0</sub>)
                  </label>
                  <input
                    type="number"
                    id="n0"
                    step="any"
                    placeholder={t('placeholderInitial')}
                    value={mainInputs.n0}
                    onChange={(e) => setMainInputs({...mainInputs, n0: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="t">
                    {t('timeElapsed')} (t)
                  </label>
                  <input
                    type="number"
                    id="t"
                    step="any"
                    placeholder={t('placeholderTime')}
                    value={mainInputs.t}
                    onChange={(e) => setMainInputs({...mainInputs, t: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="t12">
                    {t('halfLife')} (t<sub>1/2</sub>)
                  </label>
                  <input
                    type="number"
                    id="t12"
                    step="any"
                    placeholder={t('placeholderHalfLife')}
                    value={mainInputs.t12}
                    onChange={(e) => setMainInputs({...mainInputs, t12: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t('calculateButton')}
                </button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={handleMainClear}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>

            {mainResults && (
              <div style={styles.resultsCard}>
                <h3 style={{ ...styles.h3, color: colors.success }}>✅ {t('calculationResults')}</h3>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel} dangerouslySetInnerHTML={{ __html: mainResults.label + ':' }} />
                  <span style={styles.resultValue}>{mainResults.value.toFixed(6)}</span>
                </div>
                <div style={styles.formulaDisplay}>{mainResults.formula}</div>
              </div>
            )}
          </div>

          {/* Conversion Calculator */}
          <div style={styles.calculatorCard}>
            <h3 style={styles.h3}>🔄 {t('conversionCalculatorTitle')}</h3>
            <p style={{ ...styles.subtitle, marginBottom: '1.5rem' }}>{t('conversionCalculatorDesc')}</p>

            <form onSubmit={handleConversionCalculate}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="c-halflife">
                    {t('halfLife')} (t<sub>1/2</sub>)
                  </label>
                  <input
                    type="number"
                    id="c-halflife"
                    step="any"
                    placeholder={t('placeholderEnterHalfLife')}
                    value={convInputs.halflife}
                    onChange={(e) => setConvInputs({...convInputs, halflife: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="c-meanlifetime">
                    {t('meanLifetime')} (τ)
                  </label>
                  <input
                    type="number"
                    id="c-meanlifetime"
                    step="any"
                    placeholder={t('placeholderEnterMeanLifetime')}
                    value={convInputs.meanlifetime}
                    onChange={(e) => setConvInputs({...convInputs, meanlifetime: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="c-decayconstant">
                    {t('decayConstant')} (λ)
                  </label>
                  <input
                    type="number"
                    id="c-decayconstant"
                    step="any"
                    placeholder={t('placeholderEnterDecayConstant')}
                    value={convInputs.decayconstant}
                    onChange={(e) => setConvInputs({...convInputs, decayconstant: e.target.value})}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  style={styles.calculateButton}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t('convertButton')}
                </button>
                <button
                  type="button"
                  style={styles.clearButton}
                  onClick={handleConversionClear}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>

            {convResults && (
              <div style={styles.conversionResultsCard}>
                <h3 style={{ ...styles.h3, color: colors.warning }}>🔢 {t('conversionResults')}</h3>
                <div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('halfLife')} (t<sub>1/2</sub>):</span>
                    <span style={styles.resultValue}>{convResults.halflife.toFixed(8)}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('meanLifetime')} (τ):</span>
                    <span style={styles.resultValue}>{convResults.meanlifetime.toFixed(8)}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t('decayConstant')} (λ):</span>
                    <span style={styles.resultValue}>{convResults.decayconstant.toFixed(8)}</span>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.infoNote}>
              <strong>💡 {t('formulasTitle')}:</strong><br />
              t<sub>1/2</sub> = ln(2) / λ = τ × ln(2)<br />
              τ = 1 / λ = t<sub>1/2</sub> / ln(2)<br />
              λ = ln(2) / t<sub>1/2</sub> = 1 / τ
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Key Insights Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insight1')}</li>
          <li style={styles.listItem}>{t('insight2')}</li>
          <li style={styles.listItem}>{t('insight3')}</li>
          <li style={styles.listItem}>{t('insight4')}</li>
          <li style={styles.listItem}>{t('insight5')}</li>
          <li style={styles.listItem}>{t('insight6')}</li>
        </ul>
      </section>

      {/* Detailed Information Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>

        <h3 style={{ ...styles.h3, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem' }}>
          {t('detailsSubtitle1')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>

        <h3 style={{ ...styles.h3, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem' }}>
          {t('detailsSubtitle2')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsApplication1')}</li>
          <li style={styles.listItem}>{t('detailsApplication2')}</li>
          <li style={styles.listItem}>{t('detailsApplication3')}</li>
          <li style={styles.listItem}>{t('detailsApplication4')}</li>
        </ul>

        <h3 style={{ ...styles.h3, color: colors.textPrimary, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem' }}>
          {t('detailsSubtitle3')}
        </h3>
        <p style={styles.paragraph}>{t('detailsParagraph3')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBestPractice1')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice2')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice3')}</li>
          <li style={styles.listItem}>{t('detailsBestPractice4')}</li>
        </ul>
      </section>

      {/* FAQ Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.calculator.net/half-life-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://testbook.com/calculators/half-life-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://study.com/academy/lesson/half-life-facts-formula-examples-chemistry.html"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://chem.libretexts.org/Courses/Furman_University/CHM101:_Chemistry_and_Global_Awareness_(Gordon)/05:_Basics_of_Nuclear_Science/5.07:_Calculating_Half-Life"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://en.wikipedia.org/wiki/Half-life"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference5')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC3581066/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}